import { Stack, Box, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { toSentenceCase } from 'common/utils/utilFunctions';

import ButtonComp from '../../../common/components/Button/ButtonComp';
import ModalEle from '../../../common/components/Modal/ModalEle';
import SearachableDropdown from '../../../common/components/SearchableDropdown/SearachableDropdown';
import SwitchComp from '../../../common/components/Switch/SwitchComp';
import TextFieldWithLabel from '../../../common/components/TextFieldWithLabel/TextFieldWithLabel';
import { RootState } from '../../../common/store/AppStore';
import EventPayload from '../../../components/EventPayload/EventPayload';

import { createSchedule, getEvents } from './api';
import { IJobSchedulerRequest } from './api/type-def';
import ScheduleComp from './ScheduleView/ScheduleComp';

import styles from './JobScheduler.module.scss';

type Event = {
	value: number | string;
	label: string;
};
interface ICreateJobProps {
	jobName: string;
	jobDescription: string;
	eventName: any;
	eventPayload: string;
	scheduleEnabled: boolean;
}

const CreateJob = () => {
	const {
		handleSubmit,
		control,
		watch,
		reset,
		formState: { isDirty, isValid },
	} = useForm<ICreateJobProps>({ mode: 'onBlur' });

	const watchShowSchedule = watch('scheduleEnabled');
	const [scheduleValues, setScheduleValues] = useState(null);
	const [eventList, setEventList] = useState<Event[]>([]);
	const [open, setOpen] = useState<boolean>(false);

	const { t } = useTranslation();

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const buildBasePayload = (data: any) => ({
		jobName: data.jobName,
		jobDescription: data.jobDescription,
		eventPayload: JSON.parse(data.eventPayload),
		createdBy: loggedInUser?.userId as string,
		updatedBy: loggedInUser?.userId as string,
		eventName: data?.eventName?.label || 'eGetRegBooks',
	});
	const buildPayloadForManualRun = () => ({
		schedulerCronDetails: {
			frequencyType: 'one-time',
		},
	});

	const buildPayloadForSchedule = (scheduleValues: any) => ({
		schedulerCronDetails: scheduleValues,
	});

	const onSubmit = async (data: any) => {
		let payload: IJobSchedulerRequest;
		if (!data.scheduleEnabled) {
			payload = {
				...buildBasePayload(data),
				...buildPayloadForManualRun(),
			};
		} else {
			payload = {
				...buildBasePayload(data),
				...buildPayloadForSchedule(scheduleValues),
			};
		}
		const response = await createSchedule(payload);
		if (response.statusCode === 201) {
			setOpen(true);
		}
		reset();
	};

	const validationError = (errors: any) => {
		console.log(errors);
	};

	const handleClose = () => setOpen(false);

	const handleScheduleChange = (scheduleData: any) => {
		setScheduleValues(scheduleData);
	};

	useEffect(() => {
		const getAllEvents = async () => {
			const res = await getEvents({ status: 'active' });
			if (res?.responseBody) {
				const events = res.responseBody.events.map((e: any, index: number) => ({
					value: index,
					label: e.eventName,
				}));
				setEventList(events);
			}
		};
		getAllEvents();
	}, []);

	return (
		<>
			<form onSubmit={handleSubmit(onSubmit, validationError)}>
				<Stack spacing={2}>
					<Controller
						control={control}
						name="jobName"
						defaultValue=""
						rules={{
							required: { value: true, message: t('The field is required') },
							validate: {
								checkEmptySpace: (e) =>
									e.trim() !== '' || 'Empty strings not allowed.',
							},
						}}
						render={({
							field: { onChange, onBlur, value },
							fieldState: { error },
						}) => (
							<TextFieldWithLabel
								label="Name"
								name="jobName"
								placeholder="Add a name of the job scheduler task"
								onBlur={onBlur}
								value={value}
								onChange={onChange}
								className={styles['job-field-width']}
								error={!!error}
								helperText={error ? error.message : null}
							/>
						)}
					/>
					<Controller
						control={control}
						name="jobDescription"
						defaultValue=""
						rules={{
							required: { value: true, message: t('The field is required') },
						}}
						render={({
							field: { onChange, onBlur, value },
							fieldState: { error },
						}) => (
							<TextFieldWithLabel
								label="Description"
								name="jobDescription"
								placeholder="Add a description of the job"
								className={styles['job-field-width']}
								multiLine
								onBlur={onBlur}
								value={value}
								onChange={onChange}
								error={!!error}
								helperText={error ? error.message : null}
							/>
						)}
					/>

					<Controller
						control={control}
						name="eventName"
						defaultValue={null}
						render={({ field: { onChange, value } }) => (
							<SearachableDropdown
								placeholder="Type or search for the name by clicking the dropdown"
								searchList={eventList}
								value={value}
								label={toSentenceCase('Select Event Name')}
								onChange={(val) => onChange(val)}
							/>
						)}
					/>

					<Controller
						control={control}
						name="eventPayload"
						defaultValue=""
						render={({ field: { onChange, value } }) => (
							<EventPayload onChange={onChange} value={value} />
						)}
					/>

					<Controller
						control={control}
						name="scheduleEnabled"
						defaultValue={false}
						render={({ field: { onChange, value } }) => (
							<SwitchComp
								label="Do you want to schedule?"
								value={value}
								onChange={onChange}
							/>
						)}
					/>

					{watchShowSchedule && (
						<ScheduleComp handleChange={handleScheduleChange} />
					)}
					<Box className={styles['btn-grp']}>
						<ButtonComp btnType="secondary" clickHandler={() => reset()}>
							Cancel
						</ButtonComp>
						{watchShowSchedule ? (
							<ButtonComp
								btnType="primary"
								disabled={!isDirty || !isValid}
								clickHandler={() => {}}
							>
								Schedule
							</ButtonComp>
						) : (
							<ButtonComp
								btnType="primary"
								disabled={!isDirty || !isValid}
								clickHandler={() => {}}
							>
								Run
							</ButtonComp>
						)}
					</Box>
				</Stack>
			</form>
			<ModalEle open={open} handleClose={handleClose}>
				<Typography id="modal-modal-title" variant="h4" component="h2">
					{toSentenceCase('Job created Successful')}
				</Typography>
				<Typography
					id="modal-modal-description"
					className={styles['modal-desc']}
				>
					Job created!!!
				</Typography>
			</ModalEle>
		</>
	);
};

export default CreateJob;
